import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";

function formatItem(item) {
  const parts = [];
  if (item.quantity) parts.push(Number(item.quantity));
  if (item.unit) parts.push(item.unit);
  parts.push(item.name);
  return parts.join(" ");
}

export default function ShoppingListTab({ onPantryUpdate, onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [completing, setCompleting] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/shopping-list/");
      setItems(res.data.items || []);
    } catch (err) {
      console.error("Failed to fetch shopping list:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    onCountChange?.(items.length);
  }, [items, onCountChange]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      const res = await axiosInstance.post("/api/shopping-list/", { text: input.trim() });
      if (res.status === 201) {
        setItems((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        setItems((prev) => prev.map((i) => i.id === res.data.id ? res.data : i));
      }
      setInput("");
    } catch (err) {
      console.error("Failed to add item:", err);
    }
  };

  const handleToggle = async (item) => {
    const newChecked = !item.checked;
    setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, checked: newChecked } : i));
    try {
      await axiosInstance.patch(`/api/shopping-list/${item.id}/`, { checked: newChecked });
    } catch (err) {
      console.error("Failed to toggle item:", err);
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, checked: !newChecked } : i));
    }
  };

  const handleRemove = async (id) => {
    const snapshot = items;
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await axiosInstance.delete(`/api/shopping-list/${id}/`);
    } catch (err) {
      console.error("Failed to remove item:", err);
      setItems(snapshot);
    }
  };

  const handleComplete = async () => {
    const snapshot = items;
    setCompleting(true);
    setItems((prev) => prev.filter((i) => !i.checked));
    try {
      await axiosInstance.post("/api/shopping-list/complete/");
      onPantryUpdate();
    } catch (err) {
      console.error("Failed to complete shopping list:", err);
      setItems(snapshot);
    } finally {
      setCompleting(false);
    }
  };

  const handleClear = async () => {
    const snapshot = items;
    setItems([]);
    try {
      await axiosInstance.delete("/api/shopping-list/clear/");
    } catch (err) {
      console.error("Failed to clear shopping list:", err);
      setItems(snapshot);
    }
  };

  const handleSetAllChecked = async (checked) => {
    const itemsToUpdate = items.filter((item) => item.checked !== checked);
    if (!itemsToUpdate.length) return;

    const snapshot = items;
    setBulkUpdating(true);
    setItems((prev) => prev.map((item) => ({ ...item, checked })));

    try {
      await axiosInstance.post("/api/shopping-list/bulk-check/", {
        item_ids: itemsToUpdate.map((item) => item.id),
        checked,
      });
    } catch (err) {
      console.error("Failed to update all items:", err);
      setItems(snapshot);
    } finally {
      setBulkUpdating(false);
    }
  };

  const checkedCount = items.filter((i) => i.checked).length;
  const allChecked = items.length > 0 && checkedCount === items.length;
  const firstColumnItems = items.slice(0, Math.ceil(items.length / 2));
  const secondColumnItems = items.slice(Math.ceil(items.length / 2));

  const renderItems = (columnItems) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/50">
      {columnItems.map((item) => (
        <div key={item.id} className="flex items-center px-5 py-3 group">
          <button
            onClick={() => handleToggle(item)}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
              item.checked
                ? "bg-green-500 border-green-500"
                : "border-gray-300 dark:border-gray-600 group-hover:border-green-300"
            }`}
          >
            {item.checked && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <span className={`ml-3 flex-1 text-sm transition ${
            item.checked
              ? "text-gray-400 dark:text-gray-500 line-through"
              : "text-gray-700 dark:text-gray-300"
          }`}>
            {formatItem(item)}
          </span>
          <button
            onClick={() => handleRemove(item.id)}
            className="opacity-0 group-hover:opacity-100 ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400 text-xs transition-all"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/50 p-5 mb-6">
        <form onSubmit={handleAdd} className="flex gap-3">
          <input
            type="text"
            placeholder='What do you need to buy? (e.g. "3 lbs flour")'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            className="flex-1 bg-gray-50 dark:bg-gray-700 border-0 px-4 py-2.5 rounded-lg text-sm dark:text-white dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 outline-none transition"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            className="bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition"
          >
            Add
          </button>
        </form>
        {items.length > 0 && (
          <div className="flex items-center justify-between gap-4 mt-3">
            <button
              onClick={() => handleSetAllChecked(!allChecked)}
              disabled={bulkUpdating}
              className={`flex items-center text-xs font-medium transition ${
                bulkUpdating
                  ? "opacity-40 text-gray-400 cursor-not-allowed"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              <span
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                  allChecked
                    ? "bg-green-500 border-green-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {allChecked && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              <span className="ml-3">
                {bulkUpdating ? "Updating all..." : allChecked ? "Uncheck all" : "Check all"}
              </span>
            </button>
            <button
              onClick={handleClear}
              className="text-xs text-gray-400 hover:text-red-500 transition"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <span className="text-2xl">🛒</span>
          </div>
          <p className="text-gray-500 dark:text-gray-400 font-medium mb-1">Your shopping list is empty</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">Add ingredients you need to buy</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {renderItems(firstColumnItems)}
            {secondColumnItems.length > 0 && renderItems(secondColumnItems)}
          </div>

          {checkedCount > 0 && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={handleComplete}
                disabled={completing}
                className="bg-green-500 hover:bg-green-600 disabled:opacity-40 text-white font-semibold px-8 py-3 rounded-lg text-sm transition"
              >
                {completing ? "Adding to Pantry..." : `Complete (${checkedCount})`}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
